import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Icon,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Search,
  Folder,
  FolderSpecial,
  ChevronLeft,
  ChevronRight,
  PersonAdd,
  Logout,
  ExpandLess,
  ExpandMore,
  // SwapVert,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { useAuth } from '../../Hooks/AuthProvider';
import AddCustomerAccess from './AddCustomerAccess';
import ManageCustomerAccess from './ManageCustomerAccess';

// Brent branding colors
const BRAND_ORANGE = '#f05a28';
const BRAND_ORANGE_DARK = '#d84f22';
// const BRAND_TEXT = '#242424';

// Array of equipment IDs to mark as "Temp" in quantity column (original view only)
const tempEquipmentIds = [6279];

const ProjectEquipment = () => {
  const navigate = useNavigate();
  const { user, handleLogout } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [equipment, setEquipment] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Background refresh indicator for equipment
  const [isRefreshingProjects, setIsRefreshingProjects] = useState(false); // Background refresh indicator for projects
  const [loadingMessage, setLoadingMessage] = useState(''); // Progress message during fetch
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayMode, setDisplayMode] = useState('exploded'); // 'exploded' or 'original'
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [userRole, setUserRole] = useState(null);
  const [rentmanContactId, setRentmanContactId] = useState(null);
  const [contactIds, setContactIds] = useState([]);
  const [allowedProjectIds, setAllowedProjectIds] = useState([]);
  const [addAccessOpen, setAddAccessOpen] = useState(false);
  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedEqGroups, setExpandedEqGroups] = useState({});


  // Handle year navigation
  const handlePreviousYear = () => {
    handleYearChange(selectedYear - 1);
  };

  const handleNextYear = () => {
    handleYearChange(selectedYear + 1);
  };

  const getDefaultSortDirection = (field) => {
    if (field === 'quantity') return 'desc'; // larger amounts first
    return 'asc'; // names A→Z, dates earliest first
  };

  const handleSortChange = (field) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setSortDirection(getDefaultSortDirection(field));
      return field;
    });
  };

  // Initialize Firebase Functions
  useEffect(() => {
    const functions = getFunctions();
    if (process.env.NODE_ENV === 'development') {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
  }, []);

  // Get user role and customer info
  useEffect(() => {
    const getUserRole = async () => {
      if (!user) {
        setUserRole(null);
        setRentmanContactId(null);
        return;
      }

      try {
        const functions = getFunctions();
        const getUserRoleFunction = httpsCallable(functions, 'getUserRole');
        const response = await getUserRoleFunction();
        setUserRole(response.data.role || 'employee');
        setRentmanContactId(response.data.rentmanContactId || null);
        setContactIds(response.data.contactIds || []);
        setAllowedProjectIds(response.data.allowedProjectIds || []);
      } catch (error) {
        console.error('[ProjectEquipment] Error getting user role:', error);
        // Default to employee if error
        setUserRole('employee');
        setRentmanContactId(null);
      }
    };

    getUserRole();
  }, [user]);

  // Fetch projects when year is selected
  const handleYearChange = async (year) => {
    setSelectedYear(year);
    setSelectedProjectId('');
    setEquipment(null);
    setError(null);

    if (!year) {
      setProjects([]);
      return;
    }

    // Check cache first
    const cachedProjects = getCachedProjects(
      year,
      userRole,
      contactIds,
      rentmanContactId,
    );
    if (cachedProjects) {
      console.log('[ProjectEquipment] Loading projects from cache for year', year);
      setProjects(cachedProjects);
      setLoadingProjects(false);
      setIsRefreshingProjects(true); // Show refresh indicator
    } else {
      setProjects([]);
      setLoadingProjects(true);
      setIsRefreshingProjects(false);
    }

    // Always fetch fresh data in background
    try {
      const functions = getFunctions();
      const fetchProjectsByDate = httpsCallable(functions, 'fetchProjectsByDate');
      
      // Format dates: end of selected year; start of selected year minus 2 months for customers
      const baseStart = dayjs(`${year}-01-01`).startOf('year');
      const startDateISO = (userRole === 'customer'
        ? baseStart.subtract(2, 'month')
        : baseStart
      ).toISOString();
      const endDateISO = dayjs(`${year}-12-31`).endOf('year').toISOString();
      // Pass both dates and rentmanContactId if user is a customer
      const requestData = { 
        startDate: startDateISO,
        endDate: endDateISO
      };
      if (userRole === 'customer') {
        if (contactIds && contactIds.length) {
          requestData.contactIds = contactIds;
        } else if (rentmanContactId) {
          requestData.rentmanContactId = rentmanContactId;
        }
      }
      const response = await fetchProjectsByDate(requestData);
      
      if (response.data.success) {
        // Sort projects alphabetically by displayname
        const sortedProjects = [...(response.data.data || [])].sort((a, b) => {
          const nameA = (a.displayname || '').toLowerCase();
          const nameB = (b.displayname || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        // Save to cache
        saveProjectsToCache(
          year,
          userRole,
          contactIds,
          rentmanContactId,
          sortedProjects,
        );
        // Update projects state
        setProjects(sortedProjects);
      } else {
        // Only show error if we don't have cached data
        if (!cachedProjects) {
          setError('Failed to fetch projects');
        }
      }
    } catch (err) {
      console.error('[ProjectEquipment] Error fetching projects:', err);
      // Only show error if we don't have cached data
      if (!cachedProjects) {
        setError(`Failed to fetch projects: ${err.message}`);
        setProjects([]);
      }
    } finally {
      setLoadingProjects(false);
      setIsRefreshingProjects(false);
    }
  };

  // Keep customers scoped to the current calendar year
  useEffect(() => {
    if (userRole === 'customer' && selectedYear !== currentYear) {
      handleYearChange(currentYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, selectedYear]);

  // Fetch projects when component mounts
  useEffect(() => {
    if (selectedYear) {
      handleYearChange(selectedYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cache key helpers
  const PROJECT_CACHE_VERSION = 'v4';
  const getEquipmentCacheKey = (projectId) => `projectEquipment_${projectId}`;
  const getProjectsCacheKey = (year, role, contacts, primaryContact) => {
    const contactKey = (contacts && contacts.length)
      ? contacts.join('-')
      : (primaryContact || 'none');
    const roleKey = role || 'unknown';
    const projectKey = allowedProjectIds.length ? allowedProjectIds.slice().sort((a, b) => a - b).join('-') : 'all';
    return `projects_${year}_${roleKey}_${contactKey}_${projectKey}_${PROJECT_CACHE_VERSION}`;
  };

  const getProjectYear = (project) => {
    if (!project) return null;
    const start = project.planperiod_start || project.planperiodStart || project.start_date || project.startDate;
    const end = project.planperiod_end || project.planperiodEnd || project.end_date || project.endDate;
    const date = start || end;
    if (!date) return null;
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.year() : null;
  };

  // Get cached equipment data
  const getCachedEquipment = (projectId) => {
    try {
      const cacheKey = getEquipmentCacheKey(projectId);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (cacheAge < maxAge) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('[ProjectEquipment] Error reading cache:', error);
    }
    return null;
  };

  // Get cached projects data
  const getCachedProjects = (year, role, contacts, primaryContact) => {
    try {
      const cacheKey = getProjectsCacheKey(year, role, contacts, primaryContact);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (cacheAge < maxAge) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('[ProjectEquipment] Error reading projects cache:', error);
    }
    return null;
  };

  // Save equipment data to cache
  const saveEquipmentToCache = (projectId, data) => {
    try {
      const cacheKey = getEquipmentCacheKey(projectId);
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('[ProjectEquipment] Error saving equipment to cache:', error);
      // If storage quota exceeded, try to clear old entries
      if (error.name === 'QuotaExceededError') {
        clearOldCacheEntries();
      }
    }
  };

  // Save projects data to cache
  const saveProjectsToCache = (year, role, contacts, primaryContact, data) => {
    try {
      const cacheKey = getProjectsCacheKey(year, role, contacts, primaryContact);
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('[ProjectEquipment] Error saving projects to cache:', error);
      // If storage quota exceeded, try to clear old entries
      if (error.name === 'QuotaExceededError') {
        clearOldCacheEntries();
      }
    }
  };

  // Project list sorted alphabetically
  const filteredProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    return [...projects].sort((a, b) => {
      const nameA = (a.displayname || '').toLowerCase();
      const nameB = (b.displayname || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [projects]);

  const toggleProjectExpanded = (key) => {
    setExpandedProjects(prev => ({
      ...prev,
      [key]: prev[key] === false ? true : prev[key] === true ? false : false,
    }));
  };

  const toggleEqGroupExpanded = (key) => {
    setExpandedEqGroups(prev => ({
      ...prev,
      [key]: prev[key] === false ? true : prev[key] === true ? false : false,
    }));
  };

  const isProjectExpanded = (key) => expandedProjects[key] !== false;
  const isEqGroupExpanded = (key) => expandedEqGroups[key] !== false;

  // Clear old cache entries (keep last 10 equipment + last 5 years of projects)
  const clearOldCacheEntries = () => {
    try {
      const keys = Object.keys(localStorage);
      const equipmentKeys = keys.filter(key => key.startsWith('projectEquipment_'));
      const projectKeys = keys.filter(key => key.startsWith('projects_'));
      
      // Clear old equipment cache (keep last 10)
      if (equipmentKeys.length > 10) {
        const entries = equipmentKeys.map(key => {
          try {
            const cached = JSON.parse(localStorage.getItem(key));
            return { key, timestamp: cached.timestamp || 0 };
          } catch {
            return { key, timestamp: 0 };
          }
        }).sort((a, b) => a.timestamp - b.timestamp);
        
        entries.slice(0, entries.length - 10).forEach(({ key }) => {
          localStorage.removeItem(key);
        });
      }
      
      // Clear old projects cache (keep last 5 years)
      if (projectKeys.length > 5) {
        const entries = projectKeys.map(key => {
          try {
            const cached = JSON.parse(localStorage.getItem(key));
            return { key, timestamp: cached.timestamp || 0 };
          } catch {
            return { key, timestamp: 0 };
          }
        }).sort((a, b) => a.timestamp - b.timestamp);
        
        entries.slice(0, entries.length - 5).forEach(({ key }) => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      console.error('[ProjectEquipment] Error clearing old cache:', error);
    }
  };

  // Progress messages to show during fetch (fake progress)
  const progressMessages = [
    'Fetching project details...',
    'Loading subprojects...',
    'Fetching equipment items...',
    'Checking for kits...',
    'Loading kit contents...',
    'Fetching equipment groups...',
    'Processing equipment data...',
    'Organizing equipment...',
    'Finalizing data...',
    'Almost done...',
    'Cross-checking dates...',
    'Merging quantities...',
    'Applying latest updates...',
    'Tidying up results...',
    'Preparing your view...',
  ];

  // Start fake progress updates
  const startProgressUpdates = () => {
    let messageIndex = 0;
    const interval = setInterval(() => {
      setLoadingMessage(progressMessages[messageIndex] || progressMessages[progressMessages.length - 1]);
      messageIndex = (messageIndex + 1) % progressMessages.length;
    }, 2500); // Update every 2.5 seconds (10 messages over ~25 seconds)
    return interval;
  };

  // Track in-flight fetch so we can ignore stale responses when users switch projects fast
  const activeFetchIdRef = useRef(0);

  // Fetch equipment when project is selected
  const handleProjectChange = async (projectId) => {
    const fetchId = ++activeFetchIdRef.current;
    setSelectedProjectId(projectId);
    setError(null);

    if (!projectId) {
      setEquipment(null);
      return;
    }

    // Check cache first
    const cachedData = getCachedEquipment(projectId);
    let progressInterval = null;
    
    if (cachedData) {
      console.log('[ProjectEquipment] Loading from cache for project', projectId);
      setEquipment(cachedData);
      setLoadingEquipment(false);
      setIsRefreshing(true); // Show refresh indicator
      setLoadingMessage(''); // Clear any loading messages
    } else {
      setEquipment(null);
      setLoadingEquipment(true);
      setIsRefreshing(false);
      setLoadingMessage(progressMessages[0]); // Start with first message
      // Start fake progress updates
      progressInterval = startProgressUpdates();
    }

    // Always fetch fresh data in background
    try {
      const functions = getFunctions();
      const fetchProjectEquipment = httpsCallable(functions, 'fetchProjectEquipment');
      
      const response = await fetchProjectEquipment({
        projectId: parseInt(projectId),
        fetchChildren: true, // Fetch children to show kit contents
        fetchGroupNames: true, // Fetch group names to display proper names
      });
      
      // If a newer request started, ignore this result
      if (fetchId !== activeFetchIdRef.current) {
        return;
      }

      if (response.data.success && response.data.data) {
        // Save to cache
        saveEquipmentToCache(projectId, response.data.data);
        // Update equipment state
        setEquipment(response.data.data);
      } else {
        // Only show error if we don't have cached data
        if (!cachedData) {
          setError('Failed to fetch equipment');
        }
      }
    } catch (err) {
      console.error('[ProjectEquipment] Error fetching equipment:', err);
      if (fetchId !== activeFetchIdRef.current) {
        return;
      }
      // Only show error if we don't have cached data
      if (!cachedData) {
        setError(`Failed to fetch equipment: ${err.message}`);
        setEquipment(null);
      }
    } finally {
      if (fetchId !== activeFetchIdRef.current) {
        // Another fetch is now active; let it manage loading state
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        return;
      }
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setLoadingEquipment(false);
      setIsRefreshing(false);
      setLoadingMessage(''); // Clear loading message
    }
  };

  // Process equipment based on display mode
  const processedEquipment = useMemo(() => {
    if (!equipment) return { exploded: [], original: [] };

    // ORIGINAL MODE: Keep kits intact, group by equipment_group
    const originalItems = [];
    
    // Helper to add item in original mode (keep structure)
    const addOriginalItem = (item, groupTitle, groupType, groupId, groupOrder) => {
      // Skip items that are children of kits (they have a parent field)
      // These are already shown as children of their parent kit
      const hasParent = item.parent && (
        (typeof item.parent === 'string' && item.parent.trim().length > 0) ||
        (typeof item.parent === 'object' && item.parent !== null)
      );
      if (hasParent) {
        return; // Skip children - they're already included via their parent kit
      }
      
      originalItems.push({
        ...item,
        groupTitle,
        groupType,
        groupId,
        groupOrder: groupOrder !== undefined && groupOrder !== null ? groupOrder : 999999,
        equipmentGroupId: item.equipment_group ? item.equipment_group.split('/').pop() : null,
        equipmentGroupName: item.equipmentGroupName || (item.equipment_group ? `Group ${item.equipment_group.split('/').pop()}` : 'Ungrouped'),
        equipmentGroupOrder: item.equipmentGroupOrder !== undefined && item.equipmentGroupOrder !== null ? item.equipmentGroupOrder : 999999,
        order: item.order || 999999, // Default to high number if no order specified
      });
    };

    // Add project equipment (original structure)
    if (equipment.project && equipment.project.equipment && equipment.project.equipment.length > 0) {
      const projectOrder = equipment.project.order !== undefined && equipment.project.order !== null
          ? equipment.project.order
          : 999999;
      equipment.project.equipment.forEach((item) => {
        addOriginalItem(item, equipment.project.title, 'project', equipment.project.id, projectOrder);
      });
    }

    // Add subproject equipment (original structure)
    if (equipment.subprojects) {
      equipment.subprojects.forEach((subproject) => {
        if (subproject.equipment && subproject.equipment.length > 0) {
          const subprojectOrder = subproject.order !== undefined && subproject.order !== null
              ? subproject.order
              : 999999;
          subproject.equipment.forEach((item) => {
            addOriginalItem(item, subproject.title, 'subproject', subproject.id, subprojectOrder);
          });
        }
      });
    }

    // EXPLODED MODE: Flatten and merge equipment
    const flattenedItems = [];

    // Helper to add item to flattened list
    const addItem = (item, groupTitle, groupType, groupId) => {
      // Skip items that are children of kits (they have a parent field)
      // These will be added when we process their parent kit
      // Check if parent exists and is a non-empty string (e.g., "/projectequipment/123")
      // Also check if it's an object with an id property (some APIs return parent as object)
      const hasParent = item.parent && (
        (typeof item.parent === 'string' && item.parent.trim().length > 0) ||
        (typeof item.parent === 'object' && item.parent !== null)
      );
      if (hasParent) {
        return; // Skip children - they're already included via their parent kit
      }
      
      // Only treat as kit if it has children AND hasChildren is true
      // This ensures we only multiply children of actual kits, not regular items
      const isKit = item.hasChildren === true && item.children && item.children.length > 0;
      
      if (isKit) {
        // If it's a kit, add its children instead (multiply by kit quantity)
        const parentQuantity = item.quantity || item.quantity_total || 1;
        item.children.forEach((child) => {
          const childQuantity = child.quantity || child.quantity_total || 0;
          const multipliedQuantity = childQuantity * parentQuantity;
          flattenedItems.push({
            ...child,
            quantity: multipliedQuantity, // Total quantity after multiplication
            quantity_total: multipliedQuantity,
            originalQuantity: childQuantity, // Original quantity before multiplication
            kitQuantity: parentQuantity, // Number of kits (for display)
            groupTitle,
            groupType,
            groupId,
            equipmentId: extractEquipmentId(child.equipment),
          });
        });
      } else {
        // Regular item (not a kit and not a child), add it without multiplication
        // Make sure to preserve original quantity
        flattenedItems.push({
          ...item,
          originalQuantity: item.quantity || item.quantity_total || 0,
          kitQuantity: null, // Not from a kit
          groupTitle,
          groupType,
          groupId,
          equipmentId: extractEquipmentId(item.equipment),
        });
      }
    };

    // Add project equipment
    if (equipment.project && equipment.project.equipment && equipment.project.equipment.length > 0) {
      equipment.project.equipment.forEach((item) => {
        addItem(item, equipment.project.title, 'project', equipment.project.id);
      });
    }

    // Add subproject equipment
    if (equipment.subprojects) {
      equipment.subprojects.forEach((subproject) => {
        if (subproject.equipment && subproject.equipment.length > 0) {
          subproject.equipment.forEach((item) => {
            addItem(item, subproject.title, 'subproject', subproject.id);
          });
        }
      });
    }

    // Merge items by equipment ID (sum quantities, combine dates)
    const mergedItems = {};
    
    flattenedItems.forEach((item) => {
      // Use normalized equipmentId for consistent merging
      const equipmentId = item.equipmentId || extractEquipmentId(item.equipment);
      const displayName = item.displayname || item.name || `Item ${item.id}`;
      const isTemp = isTempEquipment(item);
      // For temp items, merge only when both equipmentId AND name match (case-insensitive).
      const nameKey = (displayName || "").toLowerCase().trim();
      const key = isTemp
        ? `${equipmentId || "temp-id"}::${nameKey || `item-${item.id}`}`
        : (equipmentId || `item-${item.id}`);
      
      const qty = item.quantity || item.quantity_total || 0;
      
      if (!mergedItems[key]) {
        mergedItems[key] = {
          ...item,
          displayname: displayName,
          quantity: qty, // Initialize with first item's quantity
          quantity_total: qty,
          planperiod_start: item.planperiod_start || null,
          planperiod_end: item.planperiod_end || null,
          unit_price: item.unit_price || 0,
          groups: new Set(),
          // Track kit information for display
          fromKits: item.kitQuantity ? true : false,
          kitQuantities: item.kitQuantity ? [item.kitQuantity] : [],
          originalQuantities: item.originalQuantity !== undefined && item.originalQuantity !== null ? [item.originalQuantity] : [],
        };
      } else {
        // Add to existing merged item
        mergedItems[key].quantity += qty;
        mergedItems[key].quantity_total += qty;
      }
      mergedItems[key].groups.add(`${item.groupType}-${item.groupId}`);
      
      // Track kit information
      if (item.kitQuantity) {
        mergedItems[key].fromKits = true;
        mergedItems[key].kitQuantities.push(item.kitQuantity);
      }
      // Always track original quantity (for both kit items and standalone items)
      if (item.originalQuantity !== undefined && item.originalQuantity !== null) {
        mergedItems[key].originalQuantities.push(item.originalQuantity);
      }
      
      if (item.planperiod_start) {
        const itemStart = new Date(item.planperiod_start);
        if (!mergedItems[key].planperiod_start || itemStart < new Date(mergedItems[key].planperiod_start)) {
          mergedItems[key].planperiod_start = item.planperiod_start;
        }
      }
      if (item.planperiod_end) {
        const itemEnd = new Date(item.planperiod_end);
        if (!mergedItems[key].planperiod_end || itemEnd > new Date(mergedItems[key].planperiod_end)) {
          mergedItems[key].planperiod_end = item.planperiod_end;
        }
      }
    });

    const explodedArray = Object.values(mergedItems).map((item) => ({
      ...item,
      groups: Array.from(item.groups),
    }));

    return {
      exploded: explodedArray,
      original: originalItems,
    };
  }, [equipment]);

  // Filter equipment based on display mode and search term
  const filteredEquipment = useMemo(() => {
    const items = displayMode === 'exploded' ? processedEquipment.exploded : processedEquipment.original;
    
    if (!items || items.length === 0) return [];

    // Filter by search term
    if (searchTerm) {
      // Split search term into words and match all words (AND logic)
      const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
      
      return items.filter((item) => {
        // Build searchable text from all relevant fields
        const searchableText = [
          item.displayname,
          item.name,
          item.id?.toString(),
          item.equipmentId || item.equipment,
        ]
          .filter(Boolean)
          .map(field => String(field).toLowerCase())
          .join(' ');
        
        // Check if all search words are present in the searchable text
        const matchesAllWords = searchWords.every(word => searchableText.includes(word));
        
        if (matchesAllWords) return true;
        
        // Also search in children for original mode
        if (displayMode === 'original' && item.children) {
          const childrenMatch = item.children.some((child) => {
            const childSearchableText = [
              child.displayname,
              child.name,
              child.id?.toString(),
            ]
              .filter(Boolean)
              .map(field => String(field).toLowerCase())
              .join(' ');
            
            return searchWords.every(word => childSearchableText.includes(word));
          });
          if (childrenMatch) return true;
        }
        
        return false;
      });
    }

    return items;
  }, [processedEquipment, displayMode, searchTerm]);

  // Group equipment for display based on mode
  const groupedEquipment = useMemo(() => {
    if (!equipment) return []; // Return empty if no equipment data
    
    if (displayMode === 'exploded') {
      if (filteredEquipment.length === 0) return [];
      // Exploded mode: merge all items into one list, sorted alphabetically
      const allItems = [...filteredEquipment].sort((a, b) => {
        const nameA = (a.displayname || a.name || '').toLowerCase();
        const nameB = (b.displayname || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Return as a single group
      return [{
        title: 'All Equipment',
        type: 'project',
        id: 'all',
        items: allItems,
      }];
    } else {
      // Original mode: group by project/subproject first, then by equipment_group
      const projectGroups = {};
      
      // First, initialize all project/subproject groups from the original equipment data
      // This ensures subprojects are shown even if all their items are filtered out
      if (equipment.project) {
        const { start, end } = extractPlanPeriod(equipment.project);
        const projectKey = `project-${equipment.project.id}`;
        projectGroups[projectKey] = {
          title: equipment.project.title || 'Project',
          type: 'project',
          id: equipment.project.id,
          planperiodStart: start,
          planperiodEnd: end,
          groupOrder: equipment.project.order !== undefined && equipment.project.order !== null ? equipment.project.order : 999999,
          equipmentGroups: {},
        };
      }
      
      if (equipment.subprojects) {
        equipment.subprojects.forEach((subproject) => {
          const { start, end } = extractPlanPeriod(subproject);
          const subprojectKey = `subproject-${subproject.id}`;
          projectGroups[subprojectKey] = {
            title: subproject.title || `Subproject ${subproject.id}`,
            type: 'subproject',
            id: subproject.id,
            planperiodStart: start,
            planperiodEnd: end,
            groupOrder: subproject.order !== undefined && subproject.order !== null ? subproject.order : 999999,
            equipmentGroups: {},
          };
        });
      }
      
      // Now populate groups with filtered items
      filteredEquipment.forEach((item) => {
        const groupKey = `${item.groupType}-${item.groupId}`;
        const eqGroupId = item.equipmentGroupId || 'ungrouped';
        
        // Ensure project/subproject group exists (should already exist from initialization)
        if (!projectGroups[groupKey]) {
          projectGroups[groupKey] = {
            title: item.groupTitle || 'Equipment',
            type: item.groupType || 'project',
            id: item.groupId || 'default',
            groupOrder: item.groupOrder !== undefined && item.groupOrder !== null ? item.groupOrder : 999999,
            equipmentGroups: {},
            planperiodStart: null,
            planperiodEnd: null,
          };
        }
        // Update project/subproject date range from item
        updateGroupDateRange(projectGroups[groupKey], item.planperiod_start || item.planperiodStart || item.start, item.planperiod_end || item.planperiodEnd || item.end);
        
        // Create equipment group within project/subproject if it doesn't exist
        if (!projectGroups[groupKey].equipmentGroups[eqGroupId]) {
          projectGroups[groupKey].equipmentGroups[eqGroupId] = {
            equipmentGroupId: eqGroupId,
            equipmentGroupName: item.equipmentGroupName || 'Ungrouped',
            equipmentGroupOrder: item.equipmentGroupOrder !== undefined && item.equipmentGroupOrder !== null ? item.equipmentGroupOrder : 999999,
            items: [],
          };
        }
        
        projectGroups[groupKey].equipmentGroups[eqGroupId].items.push(item);
      });

      // Convert to flat structure: project/subproject -> equipment groups -> items
      // Sort: projects first (by order), then subprojects (by order), then equipment groups by order, then items by order
      return Object.values(projectGroups)
          .map(projectGroup => ({
            ...projectGroup,
            equipmentGroups: Object.values(projectGroup.equipmentGroups)
                .sort((a, b) => {
                  const orderA = a.equipmentGroupOrder !== undefined && a.equipmentGroupOrder !== null ? a.equipmentGroupOrder : 999999;
                  const orderB = b.equipmentGroupOrder !== undefined && b.equipmentGroupOrder !== null ? b.equipmentGroupOrder : 999999;
                  return orderA - orderB;
                })
                .map(eqGroup => ({
                  ...eqGroup,
                  items: eqGroup.items.sort((a, b) => {
                    const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
                    const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
                    return orderA - orderB;
                  }),
                })),
          }))
          .sort((a, b) => {
            // Sort projects before subprojects
            if (a.type === 'project' && b.type === 'subproject') return -1;
            if (a.type === 'subproject' && b.type === 'project') return 1;
            // Within same type, sort by order
            const orderA = a.groupOrder !== undefined && a.groupOrder !== null ? a.groupOrder : 999999;
            const orderB = b.groupOrder !== undefined && b.groupOrder !== null ? b.groupOrder : 999999;
            return orderA - orderB;
          });
    }
  }, [filteredEquipment, displayMode, equipment]);

  const showSubprojectIndex = useMemo(() => {
    return !loadingEquipment &&
      groupedEquipment &&
      groupedEquipment.some(g => g.type === 'subproject') &&
      displayMode === 'original';
  }, [groupedEquipment, loadingEquipment, displayMode]);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const offset = 150; // keep header in view, smaller offset for closer positioning
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({top, behavior: 'smooth'});
    }
  };

  const selectedProject = projects.find((p) => p.id.toString() === selectedProjectId);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {userRole !== 'customer' && (
            <IconButton
              onClick={() => navigate('/resources')}
              sx={{ mr: 2, color: BRAND_ORANGE }}
            >
                <ArrowBack />
              </IconButton>
            )}
          <Typography variant="h4" sx={{ color: BRAND_ORANGE, fontWeight: 600 }}>
              Project Equipment
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Add Access Button - Only for employees */}
            {userRole === 'employee' && (
              <>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setAddAccessOpen(true)}
                  sx={{
                    backgroundColor: BRAND_ORANGE,
                    '&:hover': {
                      backgroundColor: BRAND_ORANGE_DARK,
                    },
                  }}
                >
                  Add Access
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setManageAccessOpen(true)}
                  sx={{
                    borderColor: BRAND_ORANGE,
                    color: BRAND_ORANGE,
                    '&:hover': {
                      borderColor: BRAND_ORANGE_DARK,
                      backgroundColor: 'rgba(240, 90, 40, 0.08)',
                    },
                  }}
                >
                  Manage Access
                </Button>
              </>
            )}
            {/* Logout Button - Customers only */}
            {userRole === 'customer' && (
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={handleLogout}
                sx={{
                  borderColor: BRAND_ORANGE,
                  color: BRAND_ORANGE,
                  '&:hover': {
                    borderColor: BRAND_ORANGE_DARK,
                    backgroundColor: 'rgba(240, 90, 40, 0.08)',
                  },
                }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Year Selector and Project Selector */}
        <Card sx={{ 
          p: 3, 
          mb: 3,
          backgroundColor: '#ffffff',
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)',
        }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Year display / navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {userRole === 'customer' ? (
                <Typography
                  variant="h6"
                  sx={{
                    minWidth: 80,
                    textAlign: 'center',
                    color: BRAND_ORANGE,
                    fontWeight: 600,
                  }}
                >
                  {selectedYear}
                </Typography>
              ) : (
                <>
              <IconButton
                onClick={handlePreviousYear}
                disabled={loadingProjects}
                sx={{
                  color: BRAND_ORANGE,
                  '&:hover': {
                    backgroundColor: 'rgba(240, 90, 40, 0.08)',
                  },
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  minWidth: 80,
                  textAlign: 'center',
                  color: BRAND_ORANGE,
                  fontWeight: 600,
                }}
              >
                {selectedYear}
              </Typography>
              <IconButton
                onClick={handleNextYear}
                disabled={loadingProjects}
                sx={{
                  color: BRAND_ORANGE,
                  '&:hover': {
                    backgroundColor: 'rgba(240, 90, 40, 0.08)',
                  },
                }}
              >
                <ChevronRight />
              </IconButton>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl sx={{ minWidth: 300 }} disabled={!selectedYear || loadingProjects}>
                <InputLabel sx={{ color: BRAND_ORANGE, '&.Mui-focused': { color: BRAND_ORANGE } }}>
                  Select Project
                </InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  label="Select Project"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE_DARK },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE_DARK },
                  }}
                >
                  {loadingProjects ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading projects...
                    </MenuItem>
                  ) : filteredProjects.length === 0 ? (
                    <MenuItem disabled>No projects found</MenuItem>
                  ) : (
                    filteredProjects.map((project) => (
                      <MenuItem
                        key={project.id}
                        value={project.id.toString()}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <span>{project.displayname || `Project ${project.id}`}</span>
                          {(() => {
                            const py = getProjectYear(project);
                            return py && py !== selectedYear ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {py}
                              </Typography>
                            ) : null;
                          })()}
                          {project.statusLabel && (
                            <Typography variant="caption" sx={{ color: project.blocked ? 'text.secondary' : 'text.primary' }}>
                              {project.statusLabel}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {/* Background refresh indicator for projects */}
              {loadingProjects && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: BRAND_ORANGE,
                  }}
                >
                  <CircularProgress size={14} sx={{ color: BRAND_ORANGE }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: BRAND_ORANGE }}>
                    Loading projects...
                  </Typography>
                </Box>
              )}
              {!loadingProjects && isRefreshingProjects && projects.length > 0 && (
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: BRAND_ORANGE,
                }}>
                  <CircularProgress size={14} sx={{ color: BRAND_ORANGE }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: BRAND_ORANGE }}>
                    Updating...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        {/* Project Title */}
        {selectedProject && (
          <Box sx={{ 
            mb: 3,
            pt: 1,
            pb: 1,
          }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h3" sx={{ color: BRAND_ORANGE, fontWeight: 700 }}>
                  {selectedProject.displayname || `Project ${selectedProject.id}`}
                </Typography>
                {/* Background refresh indicator (only when refreshing, not initial load) */}
                {isRefreshing && !loadingEquipment && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: BRAND_ORANGE }}>
                    <CircularProgress size={16} sx={{ color: BRAND_ORANGE }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: BRAND_ORANGE }}>
                      Updating...
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* Loading Indicator below title (when no cache) */}
              {loadingEquipment && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: BRAND_ORANGE }}>
                  <CircularProgress size={20} sx={{ color: BRAND_ORANGE }} />
                  <Typography variant="body2" sx={{ color: BRAND_ORANGE }}>
                    {loadingMessage || 'Loading equipment...'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Display Mode Toggle */}
        {equipment && (
          <Card sx={{ 
            p: 2, 
            mb: 3,
            backgroundColor: '#ffffff',
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Display Mode:
              </Typography>
              <ToggleButtonGroup
                value={displayMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) setDisplayMode(newMode);
                }}
                size="small"
              >
                <ToggleButton value="exploded">Totals View</ToggleButton>
                <ToggleButton value="original">List/PDF View</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Card>
      )}

        {/* Equipment Table */}
        {equipment && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}>
          <Card sx={{ 
            backgroundColor: '#ffffff',
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            overflow: 'visible', // Don't create scroll context - let table overflow naturally
              flex: 1,
          }}>
            {/* Search Bar */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              position: 'sticky',
              top: userRole === 'customer' ? 0 : 64, // No header offset for customers
              zIndex: 20, // Higher than table header
              backgroundColor: '#ffffff',
            }}>
              <TextField
                fullWidth
                label="Search Equipment"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>

            {/* Loading State */}
            {loadingEquipment && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Equipment Display */}
            {!loadingEquipment && groupedEquipment.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 'No equipment found matching your search.' : 'No equipment found for this project.'}
                </Typography>
              </Box>
            )}

            {!loadingEquipment && groupedEquipment.length > 0 && (
              <Box>
                {displayMode === 'exploded' ? (
                  // Exploded mode: single merged list, no grouping
                  groupedEquipment.length > 0 && (
                    <Box>
                        {renderEquipmentTable(
                          groupedEquipment[0].items,
                          0,
                          false,
                          null,
                          userRole,
                          sortField,
                          sortDirection,
                          handleSortChange,
                        )}
                    </Box>
                  )
                ) : (
                  // Original mode: project/subproject -> equipment group grouping
                  (() => {
                    const hasSubprojects = groupedEquipment.some(g => g.type === 'subproject');
                    const visibleGroups = groupedEquipment.filter((projectGroup) => {
                      // Hide project header if subprojects exist
                      return !(projectGroup.type === 'project' && hasSubprojects);
                    });
                    return visibleGroups.map((projectGroup, projectGroupIndex) => {
                    const equipmentGroups = projectGroup.equipmentGroups || [];
                    const sectionId = `section-${projectGroup.type}-${projectGroup.id}`;
                    const projKey = `${projectGroup.type}-${projectGroup.id}`;
                    const expanded = isProjectExpanded(projKey);
                    return (
                      <Box key={projKey} id={sectionId}>
                        {/* Project/Subproject Header */}
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(240, 90, 40, 0.12)',
                          borderLeft: `4px solid ${BRAND_ORANGE}`,
                          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                          mt: projectGroupIndex > 0 ? 3 : 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleProjectExpanded(projKey)}
                        >
                          {projectGroup.type === 'subproject' && (
                            <FolderSpecial sx={{ color: BRAND_ORANGE, fontSize: 28 }} />
                          )}
                          <Typography variant="h5" sx={{ color: BRAND_ORANGE, fontWeight: 700 }}>
                            {projectGroup.type === 'project'
                              ? 'Project'
                              : `Liste: ${projectGroup.title || 'Subproject'}`}
                              {projectGroup.planperiodStart && (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  ml: 1,
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  display: 'inline-block',
                                  textAlign: 'right',
                                }}
                              >
                                {formatDateRange(projectGroup.planperiodStart, projectGroup.planperiodEnd)}
                              </Typography>
                              )}
                          </Typography>
                          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                            <Icon sx={{ color: BRAND_ORANGE }}>
                              {expanded ? <ExpandMore /> : <ExpandLess />}
                            </Icon>
                          </Box>
                        </Box>
                        {expanded && (
                          equipmentGroups.length > 0 ? (
                            equipmentGroups.map((eqGroup, eqGroupIndex) => {
                              const items = eqGroup.items || [];
                              const eqKey = `${projKey}-eq-${eqGroup.equipmentGroupId}`;
                              const eqExpanded = isEqGroupExpanded(eqKey);
                              return (
                                <Box key={`eqgroup-${eqGroup.equipmentGroupId}`}>
                                  {/* Equipment Group Header */}
                                  <Box sx={{ 
                                    p: 2,
                                    backgroundColor: 'rgba(240, 90, 40, 0.08)',
                                    borderLeft: '4px solid rgba(240, 90, 40, 0.3)',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => toggleEqGroupExpanded(eqKey)}
                                  >
                                    <Folder sx={{ color: BRAND_ORANGE, fontSize: 24 }} />
                                    <Typography variant="h5" sx={{ color: BRAND_ORANGE, fontWeight: 600 }}>
                                      {eqGroup.equipmentGroupName || 'Ungrouped'}
                                    </Typography>
                                    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                      <Icon sx={{ color: BRAND_ORANGE }}>
                                        {eqExpanded ? <ExpandMore /> : <ExpandLess />}
                                      </Icon>
                                    </Box>
                                  </Box>
                                  {eqExpanded && items.length > 0 && renderEquipmentTable(items, `${projectGroupIndex}-${eqGroupIndex}`, true, projectGroup, userRole)}
                                  {/* Only show empty message if expanded */}
                                  {eqExpanded && items.length === 0 && (
                                    <Box sx={{ p: 2 }}>
                                      <Typography color="text.secondary">No equipment in this {projectGroup.type}</Typography>
                                    </Box>
                                  )}
                                  {eqGroupIndex < equipmentGroups.length - 1 && <Divider sx={{ my: 1 }} />}
                                </Box>
                              );
                            })
                          ) : (
                            // Only show "No equipment" message if it's not a project with subprojects
                            (!(projectGroup.type === 'project' && groupedEquipment.some(g => g.type === 'subproject'))) && (
                              <Box sx={{ p: 2 }}>
                                <Typography color="text.secondary">No equipment in this {projectGroup.type}</Typography>
                              </Box>
                            )
                          )
                        )}
                        {expanded && projectGroupIndex < visibleGroups.length - 1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    );
                  });
                  })()
                )}
              </Box>
            )}
          </Card>

            {/* Side quick navigation (non-floating) */}
            {showSubprojectIndex && (
              <Box
                sx={{
                  position: 'sticky',
                  top: userRole === 'customer' ? 0 : 64,
                  alignSelf: 'flex-start',
                  minWidth: 220,
                  maxWidth: 260,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 1,
                  p: 1.5,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: BRAND_ORANGE, fontWeight: 700, mb: 1 }}>
              Gå til liste
            </Typography>
                <Stack spacing={1}>
              {groupedEquipment
                .filter(g => g.type === 'subproject')
                .map(g => {
                  const sectionId = `section-${g.type}-${g.id}`;
                  return (
                    <Button
                      key={sectionId}
                      size="small"
                          variant="text"
                          sx={{
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            color: BRAND_ORANGE,
                            minWidth: 0,
                            px: 0.75,
                          }}
                          onClick={() => scrollToSection(sectionId)}
                    >
                      {g.title || `Subproject ${g.id}`}
                    </Button>
                  );
                })}
                </Stack>
            </Box>
            )}
          </Box>
        )}

        {/* Empty State */}
        {!equipment && !loadingEquipment && selectedYear && !selectedProjectId && (
          <Card sx={{ 
            p: 4,
            textAlign: 'center',
            backgroundColor: '#ffffff',
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
          }}>
            <Typography color="text.secondary">
              Select a project to view equipment
            </Typography>
          </Card>
        )}

        {/* Add Customer Access Dialog */}
        <AddCustomerAccess
          open={addAccessOpen}
          onClose={() => setAddAccessOpen(false)}
          onSuccess={() => {
            // Optionally refresh or show success message
            console.log('[ProjectEquipment] Customer access added successfully');
          }}
        />
        <ManageCustomerAccess
          open={manageAccessOpen}
          onClose={() => setManageAccessOpen(false)}
        />
      </Box>
  );
};

// Helper function to extract equipment ID from reference path (e.g., "/equipment/1234" -> "1234")
const extractEquipmentId = (equipmentRef) => {
  if (!equipmentRef) return null;
  if (typeof equipmentRef === 'string' && equipmentRef.includes('/')) {
    return equipmentRef.split('/').pop();
  }
  return equipmentRef;
};

// Helper function to check if equipment ID is in temp list
const isTempEquipment = (item) => {
  const equipmentId = extractEquipmentId(item.equipment) || item.equipmentId;
  if (!equipmentId) return false;
  return tempEquipmentIds.includes(parseInt(equipmentId));
};

const formatDateRange = (start, end) => {
  if (!start && !end) return '';
  const startStr = start ? dayjs(start).format('DD.MM.YYYY') : null;
  const endStr = end ? dayjs(end).format('DD.MM.YYYY') : null;
  if (startStr && endStr) return `${startStr} - ${endStr}`;
  return startStr || endStr || '';
};

// Normalize plan period fields from varied shapes
const extractPlanPeriod = (entity = {}) => {
  const plan = entity.planperiod || entity.planPeriod || {};
  const start =
    entity.planperiod_start ||
    entity.planperiodStart ||
    plan.start ||
    plan.startDate ||
    entity.start_date ||
    entity.startDate;
  const end =
    entity.planperiod_end ||
    entity.planperiodEnd ||
    plan.end ||
    plan.endDate ||
    entity.end_date ||
    entity.endDate;
  return { start: start || null, end: end || null };
};

  const updateGroupDateRange = (group, start, end) => {
    if (!group) return;
    const startTime = start ? new Date(start).getTime() : null;
    const endTime = end ? new Date(end).getTime() : null;
    if (startTime) {
      const currentStart = group.planperiodStart ? new Date(group.planperiodStart).getTime() : null;
      if (!currentStart || startTime < currentStart) {
        group.planperiodStart = new Date(startTime).toISOString();
      }
    }
    if (endTime) {
      const currentEnd = group.planperiodEnd ? new Date(group.planperiodEnd).getTime() : null;
      if (!currentEnd || endTime > currentEnd) {
        group.planperiodEnd = new Date(endTime).toISOString();
      }
    }
};

// Helper to merge temporary equipment lines by name in exploded view
const mergeTempEquipmentByName = (items) => {
  const merged = [];
  const tempMap = new Map();

  items.forEach(item => {
    if (isTempEquipment(item)) {
      const rawName = (item.displayname || item.name || "").trim();
      const nameKey = rawName ? rawName.toLowerCase() : null;

      // If no name, do NOT merge; just push as-is to preserve distinct rows
      if (!nameKey) {
        merged.push(item);
        return;
      }

      const existing = tempMap.get(nameKey);
      const qty = item.quantity || item.quantity_total || 0;
      const start = item.planperiod_start ? dayjs(item.planperiod_start) : null;
      const end = item.planperiod_end ? dayjs(item.planperiod_end) : null;

      if (existing) {
        existing.quantity += qty;
        existing.quantity_total += qty;
        // Expand date range if available
        if (start && (!existing.start || start.isBefore(existing.start))) {
          existing.start = start;
        }
        if (end && (!existing.end || end.isAfter(existing.end))) {
          existing.end = end;
        }
      } else {
        tempMap.set(nameKey, {
          ...item,
          displayname: rawName,
          quantity: qty,
          quantity_total: qty,
          start: start || null,
          end: end || null,
        });
      }
    } else {
      merged.push(item);
    }
  });

  // Push merged temp items back
  tempMap.forEach(value => merged.push(value));

  // Sort everything by display name (fallback to name) for consistent ordering
  return merged.sort((a, b) => {
    const nameA = (a.displayname || a.name || '').toLowerCase();
    const nameB = (b.displayname || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

// Helper function to render equipment table
const renderEquipmentTable = (
  items,
  key,
  showChildren = false,
  projectGroup = null,
  userRole = null,
  sortField = 'name',
  sortDirection = 'asc',
  onSortChange = null,
) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  // In exploded view (showChildren = false), merge temp equipment by name
  const processedItems = showChildren ? items : mergeTempEquipmentByName(items);

  const sortable = !showChildren;

  const getDateValue = (item, fallbackKey) => {
    const rawStart = item.planperiod_start || item.planperiodStart || item.start;
    const rawEnd = item.planperiod_end || item.planperiodEnd || item.end;
    const raw = fallbackKey === 'endDate' ? (rawEnd || rawStart) : (rawStart || rawEnd);
    return raw ? new Date(raw).getTime() : null;
  };

  const sortedItems = sortable
    ? [...processedItems].sort((a, b) => {
        const dir = sortDirection === 'desc' ? -1 : 1;
        const getVal = (item) => {
          switch (sortField) {
            case 'quantity':
              return item.quantity || item.quantity_total || 0;
            case 'startDate':
              return getDateValue(item, 'startDate');
            case 'endDate':
              return getDateValue(item, 'endDate');
            case 'name':
            default:
              return (item.displayname || item.name || '').toLowerCase();
          }
        };
        const valA = getVal(a);
        const valB = getVal(b);

        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB) * dir;
        }
        return (valA < valB ? -1 : 1) * dir;
      })
    : processedItems;
  
  // Table header sticky top offset
  // For customers: no header, so offset is just search bar height (~73px: 16px padding top + 16px padding bottom + ~41px input height)
  // For employees: header (64px) + search bar (~73px) = 137px
  const stickyTopOffset = userRole === 'customer' ? 73 : 137;

  const renderHeaderLabel = (field, label) => {
    const isActive = sortable && sortField === field;
    const arrow = sortDirection === 'asc' ? '▲' : '▼';
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: isActive ? BRAND_ORANGE : 'inherit' }}>
        {label}
        {sortable && (
          <Box component="span" sx={{ fontSize: '0.8rem', lineHeight: 1, color: isActive ? BRAND_ORANGE : 'inherit' }}>
            {isActive ? arrow : '↕'}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Table sx={{ 
      width: '100%',
      tableLayout: 'fixed', // Fixed layout for consistent column widths
    }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ 
            fontWeight: 600,
            borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            top: stickyTopOffset,
            zIndex: 10,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            py: 1,
          width: '55%', // Equipment column - largest
            cursor: sortable ? 'pointer' : 'default',
            color: sortable && sortField === 'name' ? BRAND_ORANGE : 'inherit',
          }}
          onClick={sortable ? () => onSortChange && onSortChange('name') : undefined}
          >
            {renderHeaderLabel('name', 'Equipment')}
          </TableCell>
          <TableCell sx={{ 
            fontWeight: 600,
            borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            top: stickyTopOffset,
            zIndex: 10,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            py: 1,
          width: '15%', // Quantity column
            cursor: sortable ? 'pointer' : 'default',
          }}
          onClick={sortable ? () => onSortChange && onSortChange('quantity') : undefined}
          >
            {renderHeaderLabel('quantity', 'Quantity')}
          </TableCell>
          <TableCell sx={{ 
            fontWeight: 600,
            borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            top: stickyTopOffset,
            zIndex: 10,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            py: 1,
          width: '15%', // Start Date column
            cursor: sortable ? 'pointer' : 'default',
          }}
          onClick={sortable ? () => onSortChange && onSortChange('startDate') : undefined}
          >
            {renderHeaderLabel('startDate', 'Start Date')}
          </TableCell>
          <TableCell sx={{ 
            fontWeight: 600,
            borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
            position: 'sticky',
            top: stickyTopOffset,
            zIndex: 10,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            py: 1,
          width: '15%', // End Date column
            cursor: sortable ? 'pointer' : 'default',
          }}
          onClick={sortable ? () => onSortChange && onSortChange('endDate') : undefined}
          >
            {renderHeaderLabel('endDate', 'End Date')}
          </TableCell>
        </TableRow>
      </TableHead>
        <TableBody>
          {sortedItems.map((item, itemIndex) => {
          const displayName = item.displayname || item.name || `Item ${item.id}`;
          const quantity = item.quantity || item.quantity_total || 0;
          
          // Format quantity based on mode:
          // - Original mode (showChildren = true): Show "1 (4)" format for kit children (handled in children rendering)
          // - Exploded mode (showChildren = false): Show only total
          // Add "(Temp)" label for temporary equipment only for employees
          const isTemp = isTempEquipment(item);
          const showTempLabel = isTemp && userRole !== 'customer';
          const quantityDisplay = showTempLabel ? `${quantity} (Temp)` : quantity;
          
          // Debug: log temporary items in exploded view
          if (!showChildren && isTemp) {
            console.log('[ProjectEquipment] Temporary item found in exploded view:', {
              displayName,
              equipment: item.equipment,
              equipmentId: item.equipmentId,
              id: item.id
            });
          }
          const startDate = item.planperiod_start || item.start || null;
          const endDate = item.planperiod_end || item.end || null;
          const hasChildren = showChildren && item.children && item.children.length > 0;

          return (
            <React.Fragment key={`${item.id || item.equipmentId || item.equipment}-${itemIndex}`}>
              {/* Parent/Kit Item */}
              <TableRow hover sx={{ backgroundColor: hasChildren ? 'rgba(53, 152, 183, 0.04)' : 'inherit' }}>
                <TableCell sx={{ py: 1 }}>
                  <Typography sx={{ fontWeight: hasChildren ? 600 : 400 }}>
                    {displayName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1 }}>{quantityDisplay}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  {startDate ? dayjs(startDate).format('DD.MM.YYYY') : '-'}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  {endDate ? dayjs(endDate).format('DD.MM.YYYY') : '-'}
                </TableCell>
              </TableRow>
              {/* Children Items */}
              {hasChildren && item.children.map((child, childIndex) => {
                const childDisplayName = child.displayname || child.name || `Item ${child.id}`;
                const childQuantity = child.quantity || child.quantity_total || 0;
                const parentKitQuantity = quantity; // Parent kit quantity
                const totalQuantity = childQuantity * parentKitQuantity; // Total after multiplying by kit quantity
                // In original mode, show "4 (16)" format for kit children (quantity per kit, then total)
                // Add "(Temp)" if child item matches tempEquipmentIds
                const isChildTemp = showChildren && isTempEquipment(child) && userRole !== 'customer';
                const childQuantityDisplay = showChildren 
                  ? isChildTemp 
                    ? `${childQuantity} (${totalQuantity}) (Temp)`
                    : `${childQuantity} (${totalQuantity})`
                  : childQuantity;
                const childStartDate = child.planperiod_start || '-';
                const childEndDate = child.planperiod_end || '-';

                return (
                  <TableRow 
                    key={`child-${child.id}-${childIndex}`}
                    hover
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          └ {childDisplayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>{childQuantityDisplay}</TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {childStartDate !== '-' ? dayjs(childStartDate).format('DD.MM.YYYY') : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {childEndDate !== '-' ? dayjs(childEndDate).format('DD.MM.YYYY') : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ProjectEquipment;

